import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Loader2, Wallet } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const formatCurrency = (amt) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);

/**
 * Salary Arrears modal — manually record amounts owed from PRIOR months
 * that are being paid out in the current cycle. Each breakdown line MUST
 * pick a source month from the dropdown (server validates this too — see
 * /api/payroll/{id}/arrears/source-months).
 *
 * Net-salary preview is computed client-side from the live row + the
 * sum-of-breakdown so the operator sees the impact before saving.
 */
export default function SalaryArrearsModal({ open, onClose, payroll, onSaved }) {
  const [sourceMonths, setSourceMonths] = useState([]);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !payroll?.id) return;
    setLoading(true);
    api.get(`/payroll/${payroll.id}/arrears/source-months`)
      .then(r => setSourceMonths(r.data?.data || []))
      .catch(e => toast.error(e.response?.data?.detail || 'Failed to load source months'))
      .finally(() => setLoading(false));

    // Pre-populate with existing breakdown (so the modal acts as an edit
    // form when arrears already exist). Empty → one blank starter row.
    const existing = payroll?.arrears_breakdown || [];
    if (existing.length) {
      setLines(existing.map(b => ({
        id: crypto.randomUUID(),
        source_payroll_id: b.source_payroll_id || '',
        amount: b.amount || '',
        note: b.note || '',
      })));
    } else {
      setLines([{ id: crypto.randomUUID(), source_payroll_id: '', amount: '', note: '' }]);
    }
  }, [open, payroll]);

  const total = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.amount) || 0), 0),
    [lines]
  );
  const previewNet = useMemo(() => {
    if (!payroll) return 0;
    return (Number(payroll.monthly_gross) || 0)
      + (Number(payroll.overtime_pay) || 0)
      + total
      - (Number(payroll.total_deductions) || 0);
  }, [payroll, total]);

  const addLine = () => setLines(l => [
    ...l, { id: crypto.randomUUID(), source_payroll_id: '', amount: '', note: '' },
  ]);

  const removeLine = (id) => setLines(l => l.filter(x => x.id !== id));

  const updateLine = (id, field, value) => setLines(l =>
    l.map(x => x.id === id ? { ...x, [field]: value } : x)
  );

  const handleSave = async () => {
    // Client-side validation before hitting the backend — friendlier
    // toasts than waiting for the API to 400.
    const cleaned = lines.filter(l => l.source_payroll_id && Number(l.amount) > 0);
    if (cleaned.length === 0) {
      toast.error('Add at least one breakdown line (source month + amount)');
      return;
    }
    const dupSources = new Set();
    for (const l of cleaned) {
      if (dupSources.has(l.source_payroll_id)) {
        toast.error('Duplicate source month — merge those lines or pick a different one');
        return;
      }
      dupSources.add(l.source_payroll_id);
    }
    setSaving(true);
    try {
      const r = await api.post(`/payroll/${payroll.id}/arrears`, {
        breakdown: cleaned.map(l => ({
          source_payroll_id: l.source_payroll_id,
          amount: Number(l.amount),
          note: l.note,
        })),
      });
      toast.success(r.data?.message || 'Arrears saved');
      onSaved?.(r.data?.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save arrears');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear all arrears from this payroll? The net salary will revert to base.')) return;
    setSaving(true);
    try {
      const r = await api.delete(`/payroll/${payroll.id}/arrears`);
      toast.success(r.data?.message || 'Arrears cleared');
      onSaved?.(r.data?.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear arrears');
    } finally {
      setSaving(false);
    }
  };

  if (!payroll) return null;

  const hasExisting = (payroll.arrears_amount || 0) > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl" data-testid="arrears-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-amber-500" />
            Salary Arrears — {payroll.employee_name}
          </DialogTitle>
          <DialogDescription>
            Record amounts owed from prior months that are being paid in {payroll.month} {payroll.year}.
            <strong> Historical payrolls are not modified.</strong>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading source months…
          </div>
        ) : sourceMonths.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            No prior payrolls on file for {payroll.employee_name}. Generate at least one earlier month before recording arrears.
          </div>
        ) : (
          <div className="space-y-3" data-testid="arrears-breakdown-list">
            {lines.map((l, idx) => (
              <div key={l.id} className="grid grid-cols-12 gap-2 items-end" data-testid={`arrears-line-${idx}`}>
                <div className="col-span-4">
                  {idx === 0 && <Label className="text-xs">Source month</Label>}
                  <Select
                    value={l.source_payroll_id}
                    onValueChange={(v) => updateLine(l.id, 'source_payroll_id', v)}
                  >
                    <SelectTrigger data-testid={`arrears-source-${idx}`}>
                      <SelectValue placeholder="Pick a prior month" />
                    </SelectTrigger>
                    <SelectContent>
                      {sourceMonths.map(s => (
                        <SelectItem key={s.payroll_id} value={s.payroll_id}>
                          {s.label}
                          <span className="text-muted-foreground text-xs ml-2">
                            (paid {formatCurrency(s.net_salary)})
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-3">
                  {idx === 0 && <Label className="text-xs">Amount (₹)</Label>}
                  <Input
                    type="number" min={1} step={0.01}
                    placeholder="e.g. 20000"
                    value={l.amount}
                    onChange={(e) => updateLine(l.id, 'amount', e.target.value)}
                    data-testid={`arrears-amount-${idx}`}
                  />
                </div>
                <div className="col-span-4">
                  {idx === 0 && <Label className="text-xs">Note (optional)</Label>}
                  <Input
                    placeholder="e.g. Cash flow shortfall"
                    value={l.note}
                    onChange={(e) => updateLine(l.id, 'note', e.target.value)}
                    data-testid={`arrears-note-${idx}`}
                  />
                </div>
                <div className="col-span-1 flex justify-end">
                  <Button
                    variant="ghost" size="icon" type="button"
                    onClick={() => removeLine(l.id)}
                    disabled={lines.length === 1}
                    data-testid={`arrears-remove-${idx}`}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button" variant="outline" size="sm"
              onClick={addLine}
              data-testid="arrears-add-line"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add another month
            </Button>

            {/* Net-salary preview — recomputes live as the operator
                edits. Matches the server-side _apply_net_salary_adjustments
                math (monthly_gross + overtime_pay + arrears - deductions). */}
            <div className="rounded-lg border bg-muted/30 p-4 mt-4 space-y-1" data-testid="arrears-preview">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Regular salary (monthly gross)</span>
                <span>{formatCurrency(payroll.monthly_gross)}</span>
              </div>
              {(payroll.overtime_pay || 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Overtime</span>
                  <span>+{formatCurrency(payroll.overtime_pay)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-amber-600 font-medium">Salary Arrears (this entry)</span>
                <span className="text-amber-600 font-medium">+{formatCurrency(total)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Deductions</span>
                <span className="text-destructive">−{formatCurrency(payroll.total_deductions)}</span>
              </div>
              <div className="flex justify-between font-semibold pt-2 border-t mt-2">
                <span>New Net Salary</span>
                <span data-testid="arrears-net-preview">{formatCurrency(previewNet)}</span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {hasExisting && (
            <Button
              type="button" variant="ghost" onClick={handleClear} disabled={saving}
              className="text-destructive"
              data-testid="arrears-clear"
            >
              Clear all arrears
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading || sourceMonths.length === 0}
            data-testid="arrears-save"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Save arrears
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
